import { useFormContext } from "react-hook-form";

import type { SignupFlow } from "@/utils/flow";
import RequiredFooter from "@/components/RequiredFooter";
import { ULThemeCheckbox } from "@/components/ULThemeCheckbox";
import AddressAutocompleteField from "@/components/form/AddressAutocompleteField";
import { ULThemeFloatingLabelField } from "@/components/form/ULThemeFloatingLabelField";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import ULThemeSelect from "@/components/ULThemeSelect";

import locales from "../../locales/en.json";
import {
  GENDER_OPTIONS,
  isValidPastAdultDate,
  personalInfoFieldsForFlow,
  TITLE_OPTIONS,
  type WizardFormValues,
} from "./types";

const AU_MOBILE = /^(\+?61|0)4\d{8}$/;
const TFN_PATTERN = /^\d{9}$/;

interface PersonalInfoStepProps {
  onContinue: () => void;
  // Flow B captures identity essentials only — given/family name and mobile.
  // Gender, title, DOB, country, address and TFN are deferred to progressive
  // profiling, so they are neither rendered nor validated here. Same
  // components and same look, fewer fields.
  flow?: SignupFlow;
}

function PersonalInfoStep({ onContinue, flow = "A" }: PersonalInfoStepProps) {
  const form = useFormContext<WizardFormValues>();
  const { watch, trigger } = form;
  const postalSameAsResidential = watch("postalSameAsResidential");
  const isFlowB = flow === "B";

  const handleContinue = async () => {
    // Only validate the fields this flow actually renders — validating a
    // hidden required field would deadlock the wizard with no visible error.
    const valid = await trigger(personalInfoFieldsForFlow(flow));
    if (valid) onContinue();
  };

  return (
    <div>
      {/* Gender + title: Flow A only. */}
      {!isFlowB && (
        <>
      <FormField
        control={form.control}
        name="gender"
        rules={{ required: locales.form.fields.gender.required }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeSelect
              {...field}
              label={locales.form.fields.gender.label}
              options={GENDER_OPTIONS}
              placeholder="Select gender"
              isRequired
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="title"
        rules={{ required: locales.form.fields.title.required }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeSelect
              {...field}
              label={locales.form.fields.title.label}
              options={TITLE_OPTIONS}
              placeholder="Select title"
              isRequired
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

        </>
      )}

      <FormField
        control={form.control}
        name="firstName"
        rules={{ required: locales.form.fields.common.required }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeFloatingLabelField
              {...field}
              label={locales.form.fields.firstName.label}
              type="text"
              autoComplete="given-name"
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="lastName"
        rules={{ required: locales.form.fields.common.required }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeFloatingLabelField
              {...field}
              label={locales.form.fields.lastName.label}
              type="text"
              autoComplete="family-name"
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      {/* DOB: Flow A only — mandatory in Flow B's progressive-profiling form. */}
      {!isFlowB && (
      <FormField
        control={form.control}
        name="dateOfBirth"
        rules={{
          required: locales.form.fields.common.required,
          validate: (value) => isValidPastAdultDate(value) || locales.form.fields.dateOfBirth.invalid,
        }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeFloatingLabelField
              {...field}
              label={locales.form.fields.dateOfBirth.label}
              type="date"
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      )}

      <FormField
        control={form.control}
        name="mobile"
        rules={{
          required: locales.form.fields.mobile.required,
          pattern: { value: AU_MOBILE, message: locales.form.fields.mobile.invalid },
        }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeFloatingLabelField
              {...field}
              label={locales.form.fields.mobile.label}
              type="tel"
              autoComplete="tel"
              title={locales.form.fields.mobile.placeholder}
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      {/* Country, addresses and TFN: Flow A only. */}
      {!isFlowB && (
        <>
      <FormField
        control={form.control}
        name="country"
        rules={{ required: locales.form.fields.common.required }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeFloatingLabelField
              {...field}
              label={locales.form.fields.country.label}
              type="text"
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="residentialAddress"
        rules={{ required: locales.form.fields.common.required }}
        render={({ field, fieldState }) => (
          <FormItem>
            <AddressAutocompleteField
              field={field}
              label={locales.form.fields.residentialAddress.label}
              autoComplete="street-address"
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="postalSameAsResidential"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start gap-2 mb-2">
              <ULThemeCheckbox
                checked={field.value}
                onCheckedChange={field.onChange}
                id="postal-same-as-residential"
              />
              <label htmlFor="postal-same-as-residential" className="text-sm theme-universal:text-body-text">
                {locales.form.fields.postalSameAsResidential.label}
              </label>
            </div>
          </FormItem>
        )}
      />

      {!postalSameAsResidential && (
        <FormField
          control={form.control}
          name="postalAddress"
          rules={{ required: locales.form.fields.common.required }}
          render={({ field, fieldState }) => (
            <FormItem>
              <AddressAutocompleteField
                field={field}
                label={locales.form.fields.postalAddress.label}
                error={!!fieldState.error}
              />
              <ULThemeFormMessage hasFormError={!!fieldState.error} />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="tfn"
        rules={{
          validate: (value) => !value || TFN_PATTERN.test(value) || locales.form.fields.tfn.invalid,
        }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemeFloatingLabelField
              {...field}
              label={locales.form.fields.tfn.label}
              type="text"
              inputMode="numeric"
              title={locales.form.fields.tfn.placeholder}
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />
      <p className="text-xs mb-4 -mt-1" style={{ color: "#6B7280" }}>
        {locales.form.fields.tfn.explainer}
      </p>

        </>
      )}

      <ULThemeButton type="button" className="w-full" onClick={handleContinue}>
        {locales.form.continueButton}
      </ULThemeButton>
      <RequiredFooter />
    </div>
  );
}

export default PersonalInfoStep;
